import BaseLayout from "../layouts/BaseLayout";
import { useEffect, useState } from "react";
import { apiRequest, VojomoLoader } from "@vojomo-core/shared";
import { Select, DatePicker } from "antd";
import type { Dayjs } from "dayjs";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell
} from "recharts";

interface AnalyticsData {
  date: string;
  requests: number;
  tokens: number;
  users: number;
  [key: string]: any;
}

export function Analytics(): React.ReactElement {
  const [loading, setLoading] = useState(true);
  const [allData, setAllData] = useState<AnalyticsData[]>([]); // Store all unfiltered data
  const [error, setError] = useState<string | null>(null);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [selectedCompanies, setSelectedCompanies] = useState<string[]>([]);
  const [dateRange, setDateRange] = useState<
    [Dayjs | null, Dayjs | null] | null
  >(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await apiRequest<{
          success: boolean;
          data: AnalyticsData[];
          message?: string;
        }>({
          subRoute: "admin",
          path: "/analytics/ai_usage",
          method: "GET"
        });

        if (response.success && response.data) {
          setAllData(response.data);
        } else {
          setError(response.message || "Failed to fetch analytics data");
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : "An error occurred");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []); // Only fetch once on mount

  // Filter data based on selected filters
  const rawData = allData.filter((row) => {
    // Filter by users
    if (selectedUsers.length > 0 && !selectedUsers.includes(row.user_id)) {
      return false;
    }
    // Filter by companies
    if (
      selectedCompanies.length > 0 &&
      !selectedCompanies.includes(row.company)
    ) {
      return false;
    }
    // Filter by date range
    if (dateRange && dateRange[0] && row.date) {
      const rowDate = new Date(row.date);
      const startDate = dateRange[0].toDate();
      if (rowDate < startDate) return false;
    }
    if (dateRange && dateRange[1] && row.date) {
      const rowDate = new Date(row.date);
      const endDate = dateRange[1].toDate();
      if (rowDate > endDate) return false;
    }
    return true;
  });

  // Aggregate data by day since BigQuery data is per user/member
  const dailyData = rawData.reduce(
    (acc, row) => {
      if (!row.date) return acc;

      // Parse date string directly to avoid timezone issues
      const dateParts = row.date.split("T")[0].split("-");
      const year = dateParts[0];
      const month = dateParts[1];
      const day = dateParts[2];
      const monthNames = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec"
      ];
      const monthIndex = parseInt(month) - 1;
      const dayKey = `${monthNames[monthIndex]} ${day}`;

      if (!acc[dayKey]) {
        acc[dayKey] = {
          date: dayKey,
          sortDate: `${year}-${month}-${day}`,
          requests: 0,
          tokens: 0,
          users: new Set()
        };
      }

      acc[dayKey].requests += row.total_documents || 0;
      acc[dayKey].tokens += row.total_tokens || 0;
      if (row.user_id) {
        acc[dayKey].users.add(row.user_id);
      }

      return acc;
    },
    {} as Record<
      string,
      {
        date: string;
        sortDate: string;
        requests: number;
        tokens: number;
        users: Set<string>;
      }
    >
  );

  // Convert to array and format
  const usageOverTime = Object.values(dailyData)
    .map((item) => ({
      date: item.date,
      requests: item.requests,
      tokens: item.tokens,
      users: item.users.size
    }))
    .sort((a, b) => {
      // Find the corresponding sortDate for proper chronological sorting
      const aEntry = Object.values(dailyData).find((d) => d.date === a.date);
      const bEntry = Object.values(dailyData).find((d) => d.date === b.date);
      return (
        new Date(aEntry?.sortDate || "").getTime() -
        new Date(bEntry?.sortDate || "").getTime()
      );
    });

  // Aggregate requests by document type from the actual columns
  const requestsByType = [
    {
      type: "Building Blocks",
      requests: rawData.reduce(
        (sum, row) => sum + (row.building_block_count || 0),
        0
      )
    },
    {
      type: "LinkedIn Posts",
      requests: rawData.reduce(
        (sum, row) => sum + (row.linkedin_post_count || 0),
        0
      )
    },
    {
      type: "Emails",
      requests: rawData.reduce((sum, row) => sum + (row.email_count || 0), 0)
    },
    {
      type: "Meeting Agendas",
      requests: rawData.reduce(
        (sum, row) => sum + (row.meeting_agenda_count || 0),
        0
      )
    },
    {
      type: "Meeting Recaps",
      requests: rawData.reduce(
        (sum, row) => sum + (row.meeting_recap_count || 0),
        0
      )
    },
    {
      type: "Journals",
      requests: rawData.reduce((sum, row) => sum + (row.journal_count || 0), 0)
    },
    {
      type: "Other Documents",
      requests: rawData.reduce(
        (sum, row) => sum + (row.other_document_count || 0),
        0
      )
    }
  ].filter((item) => item.requests > 0);

  // Calculate cost breakdown
  const totalAICost = rawData.reduce((sum, row) => sum + (row.ai_cost || 0), 0);
  const totalAudioCost = rawData.reduce(
    (sum, row) => sum + (row.audio_cost || 0),
    0
  );
  const totalImages = rawData.reduce(
    (sum, row) => sum + (row.images_generated || 0),
    0
  );

  const costBreakdown = [
    { name: "AI Costs", value: totalAICost, color: "#8b5cf6" },
    { name: "Audio Costs", value: totalAudioCost, color: "#06b6d4" }
  ].filter((item) => item.value > 0);

  // Get last 7 entries for active users trend
  const recentActiveUsers = usageOverTime.slice(0, 7).reverse();

  // Calculate summary stats
  const totalDocuments = rawData.reduce(
    (sum, row) => sum + (row.total_documents || 0),
    0
  );
  const totalTokens = rawData.reduce(
    (sum, row) => sum + (row.total_tokens || 0),
    0
  );
  const totalCost = rawData.reduce(
    (sum, row) => sum + (row.total_cost || 0),
    0
  );

  const formatNumber = (num: number): string => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  const formatCurrency = (num: number): string => {
    if (num >= 1000) return `$${(num / 1000).toFixed(1)}K`;
    return `$${num.toFixed(2)}`;
  };

  // Get unique users and companies for filters with smart cross-filtering
  // If companies are selected, only show users from those companies
  const availableUsersData =
    selectedCompanies.length > 0
      ? allData.filter((row) => selectedCompanies.includes(row.company))
      : allData;

  const uniqueUsers = Array.from(
    new Set(
      availableUsersData
        .filter((row) => row.user_name && row.user_id)
        .map((row) =>
          JSON.stringify({
            id: row.user_id,
            name: row.user_name,
            company: row.company
          })
        )
    )
  )
    .map((str) => JSON.parse(str))
    .sort((a, b) => a.name.localeCompare(b.name));

  // If users are selected, only show companies where those users work
  const availableCompaniesData =
    selectedUsers.length > 0
      ? allData.filter((row) => selectedUsers.includes(row.user_id))
      : allData;

  const uniqueCompanies = Array.from(
    new Set(
      availableCompaniesData
        .filter((row) => row.company)
        .map((row) => row.company)
    )
  ).sort();

  const stats = [
    { label: "Total Cost", value: formatCurrency(totalCost) },
    { label: "AI Cost", value: formatCurrency(totalAICost) },
    { label: "Audio Cost", value: formatCurrency(totalAudioCost) },
    { label: "Total Documents", value: formatNumber(totalDocuments) },
    { label: "Total Tokens", value: formatNumber(totalTokens) },
    { label: "Images Generated", value: formatNumber(totalImages) }
  ];

  if (loading) {
    return (
      <BaseLayout pageTitle="Analytics">
        <div className="flex items-center justify-center h-[80vh]">
          <VojomoLoader />
        </div>
      </BaseLayout>
    );
  }

  if (error) {
    return (
      <BaseLayout pageTitle="Analytics">
        <div className="flex items-center justify-center h-[80vh]">
          <div className="text-center">
            <h2 className="text-xl font-semibold text-red-600 mb-2">
              Error Loading Analytics
            </h2>
            <p className="text-gray-600">{error}</p>
          </div>
        </div>
      </BaseLayout>
    );
  }

  return (
    <BaseLayout pageTitle="Analytics">
      <h3>AI Usage Analytics</h3>

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Filter by User
          </label>
          <Select<string[]>
            mode="multiple"
            allowClear
            style={{ width: "100%" }}
            placeholder="Select users"
            value={selectedUsers}
            onChange={(value) => setSelectedUsers(value)}
            options={uniqueUsers.map((user) => ({
              label: user.name,
              value: user.id
            }))}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Filter by Company
          </label>
          <Select<string[]>
            mode="multiple"
            allowClear
            style={{ width: "100%" }}
            placeholder="Select companies"
            value={selectedCompanies}
            onChange={(value) => setSelectedCompanies(value)}
            options={uniqueCompanies.map((company) => ({
              label: company,
              value: company
            }))}
          />
        </div>

        <div className="lg:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Date Range
          </label>
          <DatePicker.RangePicker
            style={{ width: "100%" }}
            value={dateRange}
            onChange={(dates) => setDateRange(dates)}
            format="YYYY-MM-DD"
          />
        </div>
      </div>

      <div className="h-[65vh] overflow-y-auto">
        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
          {stats.map((stat, index) => (
            <div
              key={index}
              className="bg-white rounded-lg shadow p-6 border border-gray-200"
            >
              <div className="text-sm text-gray-600 mb-2">{stat.label}</div>
              <div className="text-3xl font-bold">{stat.value}</div>
            </div>
          ))}
        </div>

        {/* Charts Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* AI Requests Over Time */}
          <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
            <h2 className="text-xl font-semibold mb-4">
              AI Requests Over Time
            </h2>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={usageOverTime}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Area
                  type="monotone"
                  dataKey="requests"
                  stroke="#8b5cf6"
                  fill="#8b5cf6"
                  fillOpacity={0.6}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* Token Usage Over Time */}
          <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
            <h2 className="text-xl font-semibold mb-4">
              Token Usage Over Time
            </h2>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={usageOverTime}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis
                  tickFormatter={(value) => {
                    if (value >= 1000000)
                      return `${(value / 1000000).toFixed(1)}M`;
                    if (value >= 1000) return `${(value / 1000).toFixed(0)}K`;
                    return value.toString();
                  }}
                />
                <Tooltip
                  formatter={(value: number) => {
                    if (value >= 1000000)
                      return `${(value / 1000000).toFixed(2)}M`;
                    if (value >= 1000) return `${(value / 1000).toFixed(0)}K`;
                    return value.toString();
                  }}
                />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="tokens"
                  stroke="#06b6d4"
                  strokeWidth={2}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Requests by Document Type */}
          <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
            <h2 className="text-xl font-semibold mb-4">
              Requests by Document Type
            </h2>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={requestsByType}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="type"
                  angle={-45}
                  textAnchor="end"
                  height={100}
                />
                <YAxis />
                <Tooltip />
                <Bar dataKey="requests" fill="#8b5cf6" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Cost Breakdown */}
          <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
            <h2 className="text-xl font-semibold mb-4">Cost Breakdown</h2>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={costBreakdown}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) =>
                    `${name}: ${(percent * 100).toFixed(0)}%`
                  }
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {costBreakdown.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* Recent Active Users */}
          <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
            <h2 className="text-xl font-semibold mb-4">Recent Active Users</h2>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={recentActiveUsers}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="users" fill="#f59e0b" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Active Users Over Time */}
          <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
            <h2 className="text-xl font-semibold mb-4">Active Users Trend</h2>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={usageOverTime}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="users"
                  stroke="#10b981"
                  strokeWidth={2}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </BaseLayout>
  );
}
